import React, { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { mapIcons, toSlug } from "utils";
import { Link } from "react-router-dom";
import styles from "./WhoWeServe.module.css";
import StickyHeader from "pages/StickyHeader";
import data from "data/whoWeServeData.json";

const mappedData = data.map(mapIcons);

const WhoWeServe = (props) => {
  const { slug } = useParams();
  const activeTab = mappedData.find(
    (tab) => toSlug(tab.name) === slug?.toLowerCase()
  );

  // Data structure for all service cards
  const [openDropdown, setOpenDropdown] = useState(0);

  useEffect(() => {
    setOpenDropdown(activeTab);
    // Scroll to the section after component mounts/updates
    if (activeTab) {
      const HEADER_OFFSET = 90;
      const sectionId = `section-${toSlug(activeTab.name)}`;
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) {
          const elementTop = el.getBoundingClientRect().top + window.scrollY;
          const offsetPosition = elementTop - HEADER_OFFSET;
          window.scrollTo({ top: offsetPosition, behavior: "smooth" });
        }
      }, 100); // Small delay to ensure DOM is ready
    }
  }, [activeTab]);

  const handleDropdownToggle = (index) => {
    setOpenDropdown(openDropdown === index ? -1 : index);
  };

  const renderContent = (item) => (
    <div
      className={styles.serviceCategories}
      id={`section-${toSlug(item.name)}`} // <-- unique ID for scrolling
    >
      {/* Render all rows based on their type */}
      {item.serviceRows.map((row, rowIndex) => {
        if (row.type === "simple") {
          return (
            <div className={styles.serviceContainer} key={rowIndex}>
              {row.cards.map((card, cardIndex) => (
                <div
                  className={styles.serviceCard}
                  key={cardIndex}
                  style={
                    cardIndex < row.cards.length - 1
                      ? { borderRight: "none" }
                      : {}
                  }
                >
                  <div className={styles.cardHeader}>
                    <div className={styles.serviceIcon}>
                      <img src={card.icon} alt={card.alt} />
                    </div>
                    <h3>{card.title}</h3>
                  </div>
                  <p style={{ whiteSpace: "pre-line" }}>
                    {card.description}
                  </p>
                </div>
              ))}
            </div>
          );
        } else if (row.type === "fullWidth") {
          const card = row.cards[0];
          return (
            <div
              className={`${styles.serviceContainer} ${styles.fullWidthContainer}`}
              key={rowIndex}
            >
              <div
                className={`${styles.serviceCard} ${styles.withImage} ${
                  styles.fullWidth
                } ${card.className ? styles[card.className] : ""}`}
              >
                {card.imageFirst ? (
                  <>
                    <div className={styles.cardImageContainer}>
                      <img
                        height={card.imageHeight}
                        width={card.imageWidth}
                        src={card.image}
                        alt={card.imageAlt}
                      />
                    </div>
                    <div className={styles.cardContent}>
                      <div className={styles.cardHeader}>
                        <div className={styles.serviceIcon}>
                          <img src={card.icon} alt={card.alt} />
                        </div>
                        <h3>{card.title}</h3>
                      </div>
                      <p style={{ whiteSpace: "pre-line" }}>
                        {card.description}
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={styles.cardContent}>
                      <div className={styles.cardHeader}>
                        <div className={styles.serviceIcon}>
                          <img src={card.icon} alt={card.alt} />
                        </div>
                        <h3>{card.title}</h3>
                      </div>
                      <p style={{ whiteSpace: "pre-line" }}>
                        {card.description}
                      </p>
                    </div>
                    <div className={styles.cardImageContainer}>
                      <img
                        height={card.imageHeight}
                        width={card.imageWidth}
                        src={card.image}
                        alt={card.imageAlt}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        }
        return null;
      })}
    </div>
  );

  if (!activeTab) {
    return (
      <Navigate to={`/who-we-serve/${toSlug(mappedData[0].name)}`} replace />
    );
  }

  return (
    <main className={styles.whoWeServeSection}>
      <title>{activeTab.title}</title>
      <meta name="description" content={activeTab.description} />
      <meta
        name="keywords"
        content="credentialing, provider onboarding, healthcare compliance, automated credentialing"
      />
      <header className={styles.whoWeServeIntro}>
        <div className={styles.introContent}>
          <h1 className={styles.introTitle}>Who We Serve</h1>
          <p className={styles.introText}>
            iCredentialing supports organizations involved in delivering, managing, or supporting healthcare services. 
          </p>
          <p className={styles.introText}>
            From individual providers and clinics to large health systems, vendors, and insurance payers, our solutions are designed to streamline credentialing workflows, support regulatory compliance, and enable secure data exchange across the healthcare ecosystem. 
          </p>
        </div>
        <figure className={styles.introImage}>
          <img
            src="../first_sect_who_we_serve.png"
            alt="Smiling doctor in a white coat, glasses, and stethoscope, holds a clipboard and pill bottle, wearing blue gloves. Bright, professional setting."
          />
        </figure>
      </header>

      <StickyHeader childClass={styles.subheaderContentWrappe}>
        <nav
          className={styles.subheaderContentWrappe}
          aria-label="Service categories"
        >
          <ul className={styles.servicesNavMenuContainer}>
            {mappedData.map((ele, i) => {
              return (
                <li key={i}>
                  <Link
                    to={`/who-we-serve/${toSlug(ele.name)}`}
                    className={`${styles.servicesNavMenuItems} ${
                      toSlug(ele.name) === toSlug(activeTab.name) &&
                      styles.selected
                    }`}
                    aria-current={
                      toSlug(ele.name) === toSlug(activeTab.name)
                        ? "page"
                        : undefined
                    }
                  >
                    <img
                      src={
                        ele.slug === activeTab.slug ? ele.activeIcon : ele.icon
                      }
                      className={styles.subHeaderIcon}
                      alt=""
                      aria-hidden="true"
                    />
                    <span>{ele.name}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </StickyHeader>

      <section className={styles.desktopContent} aria-label="Service details">
        {renderContent(activeTab)}
      </section>

      <section
        className={styles.mobileContent}
        aria-label="Service categories accordion"
      >
        {mappedData.map((item, index) => (
          <article key={item.name} className={styles.accordionItem}>
            <h2
              className={`${styles.accordionHeader} ${
                openDropdown === index ? styles.accordionHeaderActive : ""
              }`}
              onClick={() => handleDropdownToggle(index)}
            >
              <div className={styles.accordionHeaderContent}>
                <img
                  src={openDropdown === index ? item.activeIcon : item.icon}
                  className={styles.accordionIcon}
                  alt=""
                  aria-hidden="true"
                />
                <span className={styles.accordionTitle}>{item.name}</span>
              </div>
              <button
                className={`${styles.accordionArrow} ${
                  openDropdown === index ? styles.accordionArrowRotated : ""
                }`}
                aria-expanded={openDropdown === index}
                aria-label={`${
                  openDropdown === index ? "Collapse" : "Expand"
                } ${item.name}`}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </h2>

            <div
              className={`${styles.accordionContent} ${
                openDropdown === index ? styles.accordionContentOpen : ""
              }`}
            >
              {renderContent(item)}
            </div>
          </article>
        ))}
      </section>

     {/* <div className={styles.serviceCategories}> */}
      {/* Render all rows based on their type */}
      {/* {serviceRows.map((row, rowIndex) => {
          if (row.type === 'simple') {
            return (
              <div className={styles.serviceContainer} key={rowIndex}>
                {row.cards.map((card, cardIndex) => (
                  <div 
                    className={styles.serviceCard} 
                    key={cardIndex}
                    style={cardIndex < row.cards.length - 1 ? {borderRight: 'none'} : {}}
                  >
                    <div className={styles.cardHeader}>
                      <div className={styles.serviceIcon}>
                        <img src={card.icon} alt={card.alt} />
                      </div>
                      <h3>{card.title}</h3>
                    </div>
                    <p>{card.description}</p>
                  </div>
                ))}
              </div>
            );
          } else if (row.type === 'fullWidth') {
            const card = row.cards[0];
            return (
              <div className={`${styles.serviceContainer} ${styles.fullWidthContainer}`} key={rowIndex}>
                <div className={`${styles.serviceCard} ${styles.withImage} ${styles.fullWidth} ${card.className ? styles[card.className] : ''}`}>
                  {card.imageFirst ? ( */}
      {/* // Image first, then content (for Telehealth)
                    <>
                      <div className={styles.cardImageContainer}>
                        <img height={card.imageHeight} width={card.imageWidth} src={card.image} alt={card.imageAlt} />
                      </div>
                      <div className={styles.cardContent}>
                        <div className={styles.cardHeader}>
                          <div className={styles.serviceIcon}>
                            <img src={card.icon} alt={card.alt} />
                          </div>
                          <h3>{card.title}</h3>
                        </div>
                        <p>{card.description}</p>
                      </div>
                    </>
                  ) : (
                    // Content first, then image (normal layout)
                    <>
                      <div className={styles.cardContent}>
                        <div className={styles.cardHeader}>
                          <div className={styles.serviceIcon}>
                            <img src={card.icon} alt={card.alt} />
                          </div>
                          <h3>{card.title}</h3>
                        </div>
                        <p>{card.description}</p>
                      </div>
                      <div className={styles.cardImageContainer}>
                      <img height={card.imageHeight} width={card.imageWidth} src={card.image} alt={card.imageAlt} />
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          }
          return null;
        })}
      </div> */}
    </main>
  );
};

export default WhoWeServe;
 